import { useState, useEffect } from 'react';
import { subscribe, unsubscribe } from '@core/constants/customEvents';
import useOrganizationTemplateImage from '@core/hooks/useOrganizationTemplateImage';
import useOrganizationTemplates from '@core/hooks/useOrganizationTemplates';
import useStudyInfo from '@core/hooks/useStudyInfo';
import blobToBase64 from '@core/utils/createBase64FromBlob';
import useSx from './sx';
import { isGetOrganizationTemplatesResponse } from './typeguards';

const useFormState = (studyId: string, organizationTemplateId?: string) => {
  const sx = useSx();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [templateImageUrl, setTemplateImageUrl] = useState<string>('');

  const [isTemplateImageLoading, setTemplateImageLoading] = useState<boolean>(true);
  const { studyInfo, isLoading: isLoadingShareUrl } = useStudyInfo({
    studyInstanceUidOrHash: studyId,
    retry: false,
  });

  const [templateId, setTemplateId] = useState<string | undefined>(
    organizationTemplateId || undefined,
  );

  const { organizationTemplates } = useOrganizationTemplates();
  const validOrganizationTemplatesResponse =
    isGetOrganizationTemplatesResponse(organizationTemplates);

  const isValidOrganizationTemplateResponseAndHasItems =
    validOrganizationTemplatesResponse && organizationTemplates.items?.length > 0;

  useEffect(() => {
    // Use first template on backend response because it is the default template
    if (!organizationTemplateId) {
      setTemplateId(
        isValidOrganizationTemplateResponseAndHasItems
          ? organizationTemplates.items[0]?.templateId
          : undefined,
      );
    }
  }, [
    isValidOrganizationTemplateResponseAndHasItems,
    organizationTemplates?.items,
    organizationTemplateId,
  ]);

  const skipLoadingTemplateImage =
    validOrganizationTemplatesResponse && (organizationTemplates.items?.length ?? 0) === 0;

  const templateImageResponse = useOrganizationTemplateImage(templateId);

  useEffect(() => {
    const ref = subscribe('evodicom.report.changeTemplate', (data) => {
      setTemplateId(data.templateId);
    });

    return () => {
      unsubscribe('evodicom.report.changeTemplate', ref);
    };
  }, []);

  useEffect(() => {
    const loadImage = async () => {
      if (
        templateImageResponse.status === 'success' &&
        (templateImageResponse.renderedImage?.size ?? 0) > 0
      ) {
        const test = await blobToBase64(templateImageResponse.renderedImage!);
        setTemplateImageUrl(test);
        setTemplateImageLoading(false);
      } else if (skipLoadingTemplateImage || templateImageResponse.status === 'error') {
        setTemplateImageLoading(false);
        setTemplateId(
          isValidOrganizationTemplateResponseAndHasItems
            ? organizationTemplates.items[0]?.templateId
            : undefined,
        );
      }
    };

    loadImage();
  }, [
    templateImageResponse.renderedImage,
    templateImageResponse.status,
    skipLoadingTemplateImage,
    validOrganizationTemplatesResponse,
  ]);

  return {
    sx,
    isLoadingShareUrl,
    templateId,
    setTemplateId,
    templateImageUrl,
    isTemplateImageLoading,
    study: studyInfo,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isTranscribing,
    setIsTranscribing,
    organizationTemplates,
  };
};

export default useFormState;
